import type { Task } from "../types";
import { visibleBoardSubtasks } from "./todayBoardFocus";

/** One stop the task dialog's arrows can open. */
export interface DialogStop {
  taskId: string;
  /** Null when the stop is the task itself. */
  subtaskId: string | null;
}

export interface DialogStopFocus {
  /** A saved filter whose only date rule is Today. */
  active: boolean;
  showCompleted: boolean;
  today: string;
}

const TASK_FOCUS = { active: true, parentOutsideToday: false } as const;

/**
 * The arrows' stops, in board reading order.
 * A Today filter stops on each task and sub-task dated today. A task that is
 * on the board only so its matching sub-task has a place is not a stop.
 * Any other view stops once per visible task, which is the previous behavior.
 */
export function boardDialogStops(
  tasks: readonly Task[],
  focus: DialogStopFocus,
): DialogStop[] {
  if (!focus.active) {
    return tasks.map((task) => ({ taskId: task.id, subtaskId: null }));
  }
  const stops: DialogStop[] = [];
  for (const task of tasks) {
    if (task.scheduled_date === focus.today) {
      stops.push({ taskId: task.id, subtaskId: null });
    }
    const subtasks = visibleBoardSubtasks(
      task.subtasks ?? [],
      focus.showCompleted,
      TASK_FOCUS,
      focus.today,
    );
    for (const subtask of subtasks) {
      stops.push({ taskId: task.id, subtaskId: subtask.id });
    }
  }
  return stops;
}

/** Drop stops whose task or sub-task is gone, keeping the snapshotted order. */
export function presentDialogStops(
  stops: readonly DialogStop[],
  tasks: readonly Task[],
): DialogStop[] {
  const taskById = new Map(tasks.map((task) => [task.id, task]));
  return stops.filter((stop) => {
    const task = taskById.get(stop.taskId);
    if (!task) return false;
    if (!stop.subtaskId) return true;
    return (task.subtasks ?? []).some((subtask) => subtask.id === stop.subtaskId);
  });
}

function wrapIndex(index: number, length: number, direction: "next" | "prev"): number {
  if (direction === "next") return index === length - 1 ? 0 : index + 1;
  return index === 0 ? length - 1 : index - 1;
}

function sameStop(
  stop: DialogStop,
  current: { taskId: string; subtaskId: string | null },
): boolean {
  return stop.taskId === current.taskId && stop.subtaskId === current.subtaskId;
}

function siblingIndex(tasks: readonly Task[], taskId: string, subtaskId: string): number {
  const task = tasks.find((item) => item.id === taskId);
  if (!task) return -1;
  return (task.subtasks ?? []).findIndex((subtask) => subtask.id === subtaskId);
}

/**
 * True when `stop` comes after an open sub-task that is not itself a stop.
 * On that card, the task stop comes before every sub-task.
 */
function stopIsAfterOpenSubtask(
  stop: DialogStop,
  cardOrder: readonly string[],
  openTaskId: string,
  openSiblingIndex: number,
  tasks: readonly Task[],
): boolean {
  const stopCard = cardOrder.indexOf(stop.taskId);
  const openCard = cardOrder.indexOf(openTaskId);
  if (stopCard === -1 || openCard === -1) return false;
  if (stopCard !== openCard) return stopCard > openCard;
  if (!stop.subtaskId) return false;
  const stopSibling = siblingIndex(tasks, stop.taskId, stop.subtaskId);
  if (stopSibling === -1) return true;
  return stopSibling > openSiblingIndex;
}

function stepFromPosition(
  stops: readonly DialogStop[],
  direction: "next" | "prev",
  isAfter: (stop: DialogStop) => boolean,
): DialogStop | null {
  if (direction === "next") return stops.find(isAfter) ?? stops[0];
  let previous: DialogStop | null = null;
  for (const stop of stops) {
    if (isAfter(stop)) break;
    previous = stop;
  }
  return previous ?? stops[stops.length - 1];
}

/**
 * The next or previous stop.
 * When `outcomes` is false, steps from task to task and ignores the open sub-task.
 * A sub-task that is not a stop is placed among its siblings, so next can be a
 * later match on the same card. A gray context task steps to the first match
 * at or after that card, and previous to the last match before it.
 * Returns null when there is nowhere else to go, or the open task is not on the board.
 */
export function stepDialogStop(
  stops: readonly DialogStop[],
  cardOrder: readonly string[],
  current: { taskId: string; subtaskId: string | null },
  direction: "next" | "prev",
  outcomes: boolean,
  tasks: readonly Task[] = [],
): DialogStop | null {
  if (stops.length === 0) return null;
  if (!outcomes) {
    const index = stops.findIndex((stop) => stop.taskId === current.taskId);
    if (index === -1) return null;
    const nextIndex = wrapIndex(index, stops.length, direction);
    if (nextIndex === index) return null;
    return stops[nextIndex];
  }

  const exact = stops.findIndex((stop) => sameStop(stop, current));
  if (exact !== -1) {
    const nextIndex = wrapIndex(exact, stops.length, direction);
    if (nextIndex === exact) return null;
    return stops[nextIndex];
  }

  const cardIndex = cardOrder.indexOf(current.taskId);
  if (cardIndex === -1) return null;

  const openSiblingIndex = current.subtaskId
    ? siblingIndex(tasks, current.taskId, current.subtaskId)
    : -1;
  if (current.subtaskId && openSiblingIndex !== -1) {
    return stepFromPosition(stops, direction, (stop) =>
      stopIsAfterOpenSubtask(stop, cardOrder, current.taskId, openSiblingIndex, tasks),
    );
  }

  if (direction === "next") {
    return (
      stops.find((stop) => cardOrder.indexOf(stop.taskId) >= cardIndex) ??
      stops[0]
    );
  }
  return stepFromPosition(
    stops,
    "prev",
    (stop) => cardOrder.indexOf(stop.taskId) >= cardIndex,
  );
}

/**
 * Whether the arrows have another stop to open.
 * On a Today filter the arrows show only when a step lands on a stop.
 */
export function dialogCanStep(
  stops: readonly DialogStop[],
  cardOrder: readonly string[],
  current: { taskId: string; subtaskId: string | null },
  outcomes: boolean,
  tasks: readonly Task[] = [],
): boolean {
  if (stops.length === 0) return false;
  if (!outcomes) {
    return (
      stops.length > 1 && stops.some((stop) => stop.taskId === current.taskId)
    );
  }
  return (
    stepDialogStop(stops, cardOrder, current, "next", true, tasks) !== null ||
    stepDialogStop(stops, cardOrder, current, "prev", true, tasks) !== null
  );
}

